// Localização: frontend/src/components/Management/LoteList.js
//Este componente lida com a exibição de lotes, incluindo as cores de destaque para estoque e validade.
//A busca agora é feita através do SearchContext no HeaderGestão

import React, { useState, useMemo } from 'react';
import { useSearch } from '../../context/SearchContext';
import LoadingIndicator from '../Common/LoadingIndicator';
import DropdownMenu from '../UI/DropdownMenu';
import { Package, Calendar, PackageCheck, ArrowRight, User, UserCircle, CircleDashed, CirclePlay, X, MoreVertical, XCircle } from 'lucide-react';
import { getPacienteFotoUrl } from '../../utils/imageUtils';

// Componente de Card para Mobile
const LoteCard = ({ lote }) => {
    const isValidadeProxima = new Date(lote.data_validade) < new Date(Date.now() + (90 * 24 * 60 * 60 * 1000));
    const estoqueColor = lote.quantidade_atual === 0 ? 'text-red-500' : lote.quantidade_atual <= 5 ? 'text-yellow-500' : 'text-green-600';
    const estoqueBgColor = lote.quantidade_atual === 0 ? 'bg-red-50' : lote.quantidade_atual <= 5 ? 'bg-yellow-50' : 'bg-green-50';
    
    return (
        <div className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200 hover:border-emerald-400 group">
            {/* Header do Card */}
            <div className={`relative bg-gradient-to-br ${estoqueBgColor} p-2 sm:p-4`}>
                <div className="flex items-start space-x-2 sm:space-x-3">
                    {/* Ícone do Lote */}
                    <div className="flex-shrink-0 mt-0.5">
                        <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-emerald-500 flex items-center justify-center shadow-md">
                            <Package className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                        </div>
                    </div>
                    
                    {/* Nome do Medicamento e Lote */}
                    <div className="flex-1 min-w-0">
                        <h3 className="text-sm sm:text-base font-bold text-gray-900 truncate group-hover:text-emerald-700 transition-colors">
                            {lote.nome_medicamento}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-600 mt-0.5 sm:mt-1">
                            <span className="font-semibold">Lote:</span> {lote.lote}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                            {lote.dose}{lote.unidade_medida}
                        </p>
                    </div>
                </div>
            </div>

            {/* Corpo do Card */}
            <div className="p-2 sm:p-4 space-y-1.5 sm:space-y-2">
                {/* Validade */}
                <div className="flex items-center text-xs sm:text-sm">
                    <Calendar className={`h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 flex-shrink-0 ${isValidadeProxima ? 'text-red-600' : 'text-emerald-600'}`} />
                    <span className="text-gray-600">
                        <span className="font-semibold">Validade:</span>{' '}
                        <span className={isValidadeProxima ? 'text-red-600 font-semibold' : 'text-gray-700'}>
                            {new Date(lote.data_validade).toLocaleDateString('pt-BR')}
                        </span>
                    </span>
                </div>

                {/* Estoque */}
                <div className="flex items-center text-xs sm:text-sm">
                    <PackageCheck className={`h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 flex-shrink-0 ${estoqueColor}`} />
                    <span className="text-gray-600">
                        <span className="font-semibold">Estoque:</span>{' '}
                        <span className={`font-semibold ${estoqueColor}`}>
                            {lote.quantidade_atual} {lote.unidade_medida}
                        </span>
                    </span>
                </div>

                {/* Data de Entrada */}
                <div className="flex items-center text-xs sm:text-sm text-gray-600">
                    <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 text-emerald-600 flex-shrink-0" />
                    <span>
                        <span className="font-semibold">Entrada:</span> {new Date(lote.data_entrada).toLocaleDateString('pt-BR')}
                    </span>
                </div>

                {/* Paciente/Alocação */}
                <div className="flex items-center text-xs sm:text-sm text-gray-600">
                    <User className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 text-emerald-600 flex-shrink-0" />
                    <div className="flex items-center gap-2">
                        {lote.paciente_nome && lote.paciente_foto ? (
                            <img
                                src={getPacienteFotoUrl(lote.paciente_foto)}
                                alt={lote.paciente_nome}
                                className="h-5 w-5 sm:h-6 sm:w-6 rounded-full object-cover border border-gray-200 flex-shrink-0"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    const iconElement = e.target.parentElement.querySelector('.fallback-icon');
                                    if (iconElement) iconElement.style.display = 'block';
                                }}
                            />
                        ) : null}
                        {(!lote.paciente_nome || !lote.paciente_foto) && (
                            <UserCircle 
                                size={20} 
                                className="text-gray-400 fallback-icon"
                            />
                        )}
                        <span>
                            <span className="font-semibold">Alocação:</span>{' '}
                            <span className={lote.paciente_nome ? 'text-gray-700' : 'text-emerald-600 font-semibold'}>
                                {lote.paciente_nome || 'Uso Geral'}
                            </span>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Componente de Header com Filtro
const FilterableHeader = ({ title, items, selectedItems, onSelectionChange, icon: Icon }) => {
    const [isOpen, setIsOpen] = useState(false);
    
    const hasSelection = selectedItems && selectedItems.length > 0;
    
    const dropdownItems = items.map(item => {
        const itemId = item.id || item.value;
        // Comparar convertendo ambos para string para garantir consistência
        const isSelected = selectedItems?.some(selectedId => String(selectedId) === String(itemId));
        
        return {
            id: itemId,
            label: item.label || item.name,
            ...(item.image ? { image: item.image, imageAlt: item.label } : { icon: item.icon, iconColor: item.iconColor }),
            textColor: isSelected ? 'text-emerald-600 font-semibold' : 'text-gray-700',
            className: isSelected ? 'bg-emerald-50' : '',
            onClick: () => {
                if (isSelected) {
                    onSelectionChange(selectedItems.filter(id => String(id) !== String(itemId)));
                } else {
                    onSelectionChange([...(selectedItems || []), itemId]);
                }
            }
        };
    });
    
    // Adicionar opção "Limpar Filtro" se houver seleção
    if (hasSelection) {
        dropdownItems.unshift({
            id: 'clear',
            label: 'Limpar Filtro',
            icon: X,
            iconColor: 'text-red-600',
            textColor: 'text-red-600',
            onClick: () => {
                onSelectionChange([]);
                setIsOpen(false);
            }
        });
    }
    
    return (
        <th className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider relative ${isOpen ? 'z-[9998]' : 'z-10'}`}>
            <div className="flex items-center justify-between gap-2">
                <span>{title}</span>
                <div className={`relative ${isOpen ? 'z-[9998]' : 'z-10'}`}>
                    <DropdownMenu
                        trigger={
                            <button
                                type="button"
                                className="flex items-center justify-center p-1 rounded hover:bg-gray-100 transition-colors"
                            >
                            {hasSelection ? (
                                <CirclePlay size={16} className="text-emerald-600" />
                            ) : (
                                <CircleDashed size={16} className="text-gray-400" />
                            )}
                            </button>
                        }
                        items={dropdownItems}
                        position="bottom-left"
                        isOpen={isOpen}
                        onOpenChange={setIsOpen}
                    />
                </div>
            </div>
        </th>
    );
};

const LoteList = ({ 
    data, 
    isLoading,
    onCancelarLote
}) => {
    // 🎯 Usar SearchContext diretamente
    const { searchTerm } = useSearch();
    
    // Estados de filtro
    const [selectedPacientes, setSelectedPacientes] = useState([]);
    const [selectedMedicamentos, setSelectedMedicamentos] = useState([]);
    const [selectedValidades, setSelectedValidades] = useState([]);
    
    // Extrair listas únicas de pacientes e medicamentos
    const pacientesUnicos = useMemo(() => {
        const pacientesMap = new Map();
        data.forEach(lote => {
            if (lote.paciente_nome && lote.paciente_id) {
                if (!pacientesMap.has(lote.paciente_id)) {
                    pacientesMap.set(lote.paciente_id, {
                        id: lote.paciente_id,
                        name: lote.paciente_nome,
                        foto: lote.paciente_foto
                    });
                }
            }
        });
        // Adicionar "Uso Geral" como opção
        pacientesMap.set('uso_geral', {
            id: 'uso_geral',
            name: 'Uso Geral',
            foto: null
        });
        return Array.from(pacientesMap.values());
    }, [data]);
    
    const medicamentosUnicos = useMemo(() => {
        const medicamentosMap = new Map();
        data.forEach(lote => {
            if (lote.medicamento_id && lote.nome_medicamento) {
                if (!medicamentosMap.has(lote.medicamento_id)) {
                    medicamentosMap.set(lote.medicamento_id, {
                        id: lote.medicamento_id,
                        name: `${lote.nome_medicamento} (${lote.dose}${lote.unidade_medida})`
                    });
                }
            }
        });
        return Array.from(medicamentosMap.values());
    }, [data]);
    
    // Opções de filtro por validade
    const opcoesValidade = useMemo(() => {
        return [
            { id: 'vencido', label: 'Vencido', descricao: 'Lotes já vencidos' },
            { id: 'proximo_vencer_30', label: 'Vence em 30 dias', descricao: 'Vence nos próximos 30 dias' },
            { id: 'proximo_vencer_90', label: 'Vence em 90 dias', descricao: 'Vence nos próximos 90 dias' },
            { id: 'valido', label: 'Válido', descricao: 'Válido por mais de 90 dias' }
        ];
    }, []);
    
    // Aplicar filtros
    const filteredData = useMemo(() => {
        let filtered = data;
        
        // Filtro por pacientes
        if (selectedPacientes.length > 0) {
            filtered = filtered.filter(lote => {
                return selectedPacientes.some(selectedId => {
                    const selectedIdStr = String(selectedId);
                    
                    // Se selecionou "uso_geral", incluir lotes sem paciente
                    if (selectedIdStr === 'uso_geral') {
                        return !lote.paciente_id;
                    }
                    
                    // Caso contrário, verificar se o paciente_id do lote corresponde
                    const lotePacienteId = lote.paciente_id ? String(lote.paciente_id) : null;
                    return selectedIdStr === lotePacienteId;
                });
            });
        }
        
        // Filtro por medicamentos
        if (selectedMedicamentos.length > 0) {
            filtered = filtered.filter(lote => {
                const loteMedId = lote.medicamento_id ? String(lote.medicamento_id) : null;
                return selectedMedicamentos.some(selectedId => 
                    String(selectedId) === loteMedId
                );
            });
        }
        
        // Filtro por validade
        if (selectedValidades.length > 0) {
            const hoje = new Date();
            hoje.setHours(0, 0, 0, 0); // Resetar horas para comparação de datas
            const em30Dias = new Date(hoje.getTime() + (30 * 24 * 60 * 60 * 1000));
            const em90Dias = new Date(hoje.getTime() + (90 * 24 * 60 * 60 * 1000));
            
            filtered = filtered.filter(lote => {
                const dataValidade = new Date(lote.data_validade);
                dataValidade.setHours(0, 0, 0, 0); // Resetar horas para comparação
                
                return selectedValidades.some(categoria => {
                    switch (categoria) {
                        case 'vencido':
                            return dataValidade < hoje;
                        case 'proximo_vencer_30':
                            return dataValidade >= hoje && dataValidade <= em30Dias;
                        case 'proximo_vencer_90':
                            return dataValidade > em30Dias && dataValidade <= em90Dias;
                        case 'valido':
                            return dataValidade > em90Dias;
                        default:
                            return false;
                    }
                });
            });
        }
        
        return filtered;
    }, [data, selectedPacientes, selectedMedicamentos, selectedValidades]);
    
    return (
        <div className="mt-4">
            {isLoading ? (
                <LoadingIndicator />
            ) : data.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl shadow-lg">
                    <p className="text-gray-500 text-sm sm:text-lg">
                        {searchTerm ? `Nenhum lote encontrado para "${searchTerm}".` : 'Nenhum lote cadastrado.'}
                    </p>
                </div>
            ) : (
                <>
                    {/* Cards para Mobile (até md) */}
                    {filteredData.length > 0 && (
                        <div className="grid grid-cols-2 md:hidden gap-2 sm:gap-4">
                            {filteredData.map((lote) => (
                                <LoteCard key={lote.id} lote={lote} />
                            ))}
                        </div>
                    )}

                    {/* Tabela para Desktop (md e acima) - Sempre visível para permitir remover filtros */}
                    <div className="hidden md:block bg-white shadow sm:rounded-lg">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 relative">
                                <tr>
                                    <FilterableHeader
                                        title="Medicamento"
                                        items={medicamentosUnicos.map(med => ({
                                            id: med.id,
                                            label: med.name,
                                            icon: Package,
                                            iconColor: 'text-gray-600'
                                        }))}
                                        selectedItems={selectedMedicamentos}
                                        onSelectionChange={setSelectedMedicamentos}
                                        icon={Package}
                                    />
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lote</th>
                                    <FilterableHeader
                                        title="Paciente/Alocação"
                                        items={pacientesUnicos.map(pac => ({
                                            id: pac.id,
                                            label: pac.name,
                                            image: pac.foto ? getPacienteFotoUrl(pac.foto) : null,
                                            icon: UserCircle,
                                            iconColor: 'text-gray-400'
                                        }))}
                                        selectedItems={selectedPacientes}
                                        onSelectionChange={setSelectedPacientes}
                                        icon={User}
                                    />
                                    <FilterableHeader
                                        title="Validade"
                                        items={opcoesValidade.map(opcao => ({
                                            id: opcao.id,
                                            label: opcao.label,
                                            name: opcao.descricao,
                                            icon: Calendar,
                                            iconColor: opcao.id === 'vencido' ? 'text-red-600' : 
                                                      opcao.id === 'proximo_vencer_30' ? 'text-orange-600' :
                                                      opcao.id === 'proximo_vencer_90' ? 'text-yellow-600' : 'text-green-600'
                                        }))}
                                        selectedItems={selectedValidades}
                                        onSelectionChange={setSelectedValidades}
                                        icon={Calendar}
                                    />
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estoque Atual</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entrada</th>
                                    {onCancelarLote && (
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredData.map((lote) => (
                                    <tr key={lote.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{lote.nome_medicamento} ({lote.dose}{lote.unidade_medida})</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{lote.lote}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <div className="flex items-center gap-2">
                                                {lote.paciente_nome && lote.paciente_foto ? (
                                                    <img
                                                        src={getPacienteFotoUrl(lote.paciente_foto)}
                                                        alt={lote.paciente_nome}
                                                        className="h-8 w-8 rounded-full object-cover border border-gray-200 flex-shrink-0"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            const iconElement = e.target.parentElement.querySelector('.fallback-icon');
                                                            if (iconElement) iconElement.style.display = 'block';
                                                        }}
                                                    />
                                                ) : null}
                                                {(!lote.paciente_nome || !lote.paciente_foto) && (
                                                    <UserCircle 
                                                        size={20} 
                                                        className="text-gray-400 fallback-icon"
                                                    />
                                                )}
                                                {lote.paciente_nome ? (
                                                    <span className="text-gray-700 font-medium">{lote.paciente_nome}</span>
                                                ) : (
                                                    <span className="text-emerald-600 font-semibold">Uso Geral</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${new Date(lote.data_validade) < new Date(Date.now() + (90 * 24 * 60 * 60 * 1000)) ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                                            {new Date(lote.data_validade).toLocaleDateString('pt-BR')}
                                        </td>
                                        <td className={`px-6 py-4 whitespace-nowrap text-sm font-semibold ${lote.quantidade_atual === 0 ? 'text-red-500' : lote.quantidade_atual <= 5 ? 'text-yellow-500' : 'text-green-600'}`}>
                                            {lote.quantidade_atual} {lote.unidade_medida}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(lote.data_entrada).toLocaleDateString('pt-BR')}
                                        </td>
                                        {onCancelarLote && (
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                {lote.status !== 'Cancelado' && (
                                                    <DropdownMenu
                                                        trigger={
                                                            <button
                                                                type="button"
                                                                className="p-1 rounded hover:bg-gray-100 transition-colors"
                                                            >
                                                                <MoreVertical size={16} className="text-gray-400" />
                                                            </button>
                                                        }
                                                        items={[
                                                            {
                                                                id: 'cancelar',
                                                                label: 'Cancelar Lote',
                                                                icon: XCircle,
                                                                iconColor: 'text-red-600',
                                                                textColor: 'text-red-600',
                                                                onClick: () => {
                                                                    if (window.confirm('Tem certeza que deseja cancelar este lote? Esta ação não pode ser desfeita.')) {
                                                                        onCancelarLote(lote.id);
                                                                    }
                                                                }
                                                            }
                                                        ]}
                                                        position="bottom-right"
                                                    />
                                                )}
                                                {lote.status === 'Cancelado' && (
                                                    <span className="text-xs text-red-600 font-semibold">Cancelado</span>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default LoteList;